import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, Shield, LogOut, ExternalLink, Heart, Smartphone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";

const AccountLinks = ({ onNav }) => {
  const { user, isStaff, logout } = useAuth();
  const nav = useNavigate();
  if (!user)
    return (
      <>
        <Link to="/login" onClick={onNav} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-login-link">Log in</Link>
        <Link to="/register" onClick={onNav} className="btn-gold !py-2.5 !px-5 !text-sm" data-testid="nav-start-btn">Start now</Link>
      </>
    );
  return (
    <>
      {isStaff && (
        <Link to="/admin" onClick={onNav} className="flex items-center gap-1.5 text-sm text-gold-light hover:text-white" data-testid="nav-admin-link"><Shield size={15} />Admin</Link>
      )}
      <Link to="/dashboard" onClick={onNav} className="flex items-center gap-1.5 text-sm text-slate-200 hover:text-white" data-testid="nav-dashboard-link"><LayoutDashboard size={15} />Dashboard</Link>
      <button onClick={async () => { await logout(); onNav?.(); nav("/"); }} className="text-slate-400 hover:text-white" aria-label="Log out" data-testid="nav-logout-btn"><LogOut size={16} /></button>
    </>
  );
};

const ExternalLinks = ({ prefix = "nav" }) => (
  <>
    <a href="https://www.toddcoconato.com" target="_blank" rel="noopener noreferrer" className="btn-outline !py-2 !px-4 !text-sm inline-flex items-center gap-1.5" data-testid={`${prefix}-toddcoconato-link`}>ToddCoconato.com <ExternalLink size={13} /></a>
    <a href="https://toddcoconato.com/app/" target="_blank" rel="noopener noreferrer" className="btn-outline !py-2 !px-4 !text-sm inline-flex items-center gap-1.5 !border-[#D4AF37] !text-gold-light" data-testid={`${prefix}-app-btn`}><Smartphone size={14} /> Download Our App</a>
    <a href="https://www.toddcoconato.com/give" target="_blank" rel="noopener noreferrer" className="btn-gold !py-2 !px-5 !text-sm inline-flex items-center gap-1.5" data-testid={`${prefix}-give-btn`}><Heart size={14} /> Give</a>
  </>
);

const DEFAULT_NAV = [["Home", "/"], ["About", "/about"], ["Free Discipleship Videos", "/videos"], ["Podcast", "/podcast"], ["Courses", "/courses"], ["Coaching", "/coaching"], ["Partnership", "/partnership"], ["Go Deeper", "/resources"], ["Store", "/store"], ["Events", "https://www.toddcoconato.com/events"], ["Free Resources", "/blog"], ["Contact", "/contact"]].map(([label, to]) => ({ label, to }));

const slug = (l) => l.toLowerCase().replace(/\s+/g, "-");

const NavItem = ({ i, className, ...rest }) => i.to.startsWith("http")
  ? <a href={i.to} target="_blank" rel="noopener noreferrer" className={className({ isActive: false })} {...rest}>{i.label}</a>
  : <NavLink to={i.to} end={i.to === "/"} className={className} {...rest}>{i.label}</NavLink>;

const PopMenu = ({ open, items, close }) => (
  <div className={`fixed inset-0 -z-10 bg-black/[0.97] backdrop-blur-xl overflow-y-auto transition-[transform,opacity] duration-500 ease-out ${open ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`} aria-hidden={!open} data-testid="pop-menu">
    <div className="min-h-full flex flex-col items-center justify-center text-center px-6 pt-36 sm:pt-44 xl:pt-56 pb-16">
      <nav className="flex flex-col items-center gap-2 sm:gap-3" aria-label="Full menu">
        {[...items, { label: "ToddCoconato.com", to: "https://www.toddcoconato.com" }].map((i, n) => (
          <NavItem key={i.to + i.label} i={i} onClick={close} tabIndex={open ? 0 : -1} style={{ transitionDelay: open ? `${120 + n * 35}ms` : "0ms" }}
            className={({ isActive }) => `font-serif text-2xl sm:text-4xl transition-[color,transform,opacity] duration-500 ${open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"} ${isActive ? "text-gold-light" : "text-white hover:text-gold-light"}`}
            data-testid={`pop-nav-link-${slug(i.label)}`} />
        ))}
      </nav>
      <div className="divider-gold w-40 my-10" />
      <div className="flex flex-wrap justify-center gap-3"><ExternalLinks prefix="pop" /></div>
      <div className="flex flex-wrap justify-center items-center gap-6 mt-8"><AccountLinks onNav={close} /></div>
    </div>
  </div>
);

export default function Navbar() {
  const { settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const items = settings?.nav?.length ? settings.nav : DEFAULT_NAV;
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  const linkCls = ({ isActive }) => `relative py-1 whitespace-nowrap text-[13px] 2xl:text-[15px] font-bold uppercase tracking-[0.06em] 2xl:tracking-[0.12em] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-[#D4AF37] after:transition-[width] after:duration-300 ${isActive ? "text-gold-light after:w-full" : "text-white hover:text-gold-light after:w-0 hover:after:w-full"}`;
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color] duration-500 ${scrolled ? "bg-black/95" : "bg-black"}`} data-testid="site-navbar">
      <Link to="/partnership" className="block text-center text-[13px] sm:text-sm py-2 px-4 text-white bg-gradient-to-r from-[#D7191C] via-[#B8860B] to-[#D4AF37] hover:brightness-110 transition-[filter]" data-testid="announcement-bar">
        Romans 12:2 — Be transformed by the renewing of your mind. <span className="underline underline-offset-4">Become a Partner</span>
      </Link>
      <div className="relative max-w-[1680px] mx-auto px-5 sm:px-8 lg:px-10 flex items-center justify-between gap-6 h-[72px]">
        <Link to="/" aria-label="The Transformed Mind Life home" ><span className="sm:hidden"><Logo compact /></span><span className="hidden sm:block"><Logo /></span></Link>
        <button onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
          className="absolute left-1/2 -translate-x-1/2 top-2 z-10 w-14 h-14 rounded-full bg-gradient-to-br from-[#E8C766] to-[#B8860B] text-black flex items-center justify-center shadow-[0_8px_30px_rgba(212,175,55,0.45)] hover:scale-105 active:scale-95 transition-transform"
          data-testid="pop-menu-btn">
          <span className={`transition-transform duration-300 ${open ? "rotate-90" : ""}`}>{open ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}</span>
        </button>
        <div className="hidden xl:flex items-center gap-4"><ExternalLinks /><span className="w-px h-6 bg-white/20" /><AccountLinks /></div>
        <div className="xl:hidden flex items-center ml-auto">
          <a href="https://www.toddcoconato.com/give" target="_blank" rel="noopener noreferrer" className="btn-gold !py-1.5 !px-4 !text-sm inline-flex items-center gap-1" data-testid="mobile-header-give-btn"><Heart size={13} /> Give</a>
        </div>
      </div>
      <PopMenu open={open} items={items} close={() => setOpen(false)} />
      <nav className="hidden xl:flex justify-center items-center gap-5 2xl:gap-8 h-[52px] border-t border-[#D4AF37]/25 bg-[#050811]" aria-label="Main" data-testid="main-nav">
        {items.map((i) => (
          <NavItem key={i.to + i.label} i={i} className={linkCls} data-testid={`nav-link-${slug(i.label)}`} />
        ))}
      </nav>
    </header>
  );
}
